import {forwardRef, type HTMLAttributes} from 'react';
import {Check} from 'lucide-react';
import {cva, type VariantProps} from 'class-variance-authority';
import {cn} from '@common-web/lib/utils';

/**
 * Variant styling: `primary` renders the dots/connectors in the theme primary
 * color (cbs-portal's orange wizard dots); `default` keeps the legacy
 * blue/emerald treatment.
 */
const stepDotVariants = cva(
  'w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold transition-colors',
  {
    variants: {
      variant: {
        default: '',
        primary: '',
      },
      state: {
        done: '',
        active: '',
        upcoming: 'bg-gray-200 text-gray-500',
      },
    },
    compoundVariants: [
      {variant: 'default', state: 'done', className: 'bg-emerald-500 text-white'},
      {variant: 'default', state: 'active', className: 'bg-brand-blue text-white'},
      {variant: 'primary', state: 'done', className: 'bg-primary text-primary-foreground'},
      {variant: 'primary', state: 'active', className: 'bg-primary text-primary-foreground'},
    ],
    defaultVariants: {variant: 'default', state: 'upcoming'},
  },
);

const stepLabelVariants = cva('text-sm font-medium', {
  variants: {
    variant: {
      default: '',
      primary: '',
    },
    state: {
      done: 'text-gray-500',
      active: '',
      upcoming: 'text-gray-500',
    },
  },
  compoundVariants: [
    {variant: 'default', state: 'active', className: 'text-brand-navy'},
    {variant: 'primary', state: 'active', className: 'text-foreground'},
  ],
  defaultVariants: {variant: 'default', state: 'upcoming'},
});

const stepConnectorVariants = cva('w-12 h-0.5 mx-3', {
  variants: {
    variant: {
      default: '',
      primary: '',
    },
    state: {
      done: '',
      active: 'bg-gray-200',
      upcoming: 'bg-gray-200',
    },
  },
  compoundVariants: [
    {variant: 'default', state: 'done', className: 'bg-emerald-500'},
    {variant: 'primary', state: 'done', className: 'bg-primary'},
  ],
  defaultVariants: {variant: 'default', state: 'upcoming'},
});

type StepState = 'done' | 'active' | 'upcoming';

function stepState(index: number, currentStep: number): StepState {
  return index < currentStep ? 'done' : index === currentStep ? 'active' : 'upcoming';
}

export interface StepIndicatorProps
  extends HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof stepDotVariants> {
  steps: readonly string[];
  currentStep: number;
}

const StepIndicator = forwardRef<HTMLDivElement, StepIndicatorProps>(
  ({steps, currentStep, variant = 'default', className, ...props}, ref) => (
    <div ref={ref} className={cn('bg-white border-b border-gray-100', className)} {...props}>
      <div className='px-8 py-4 max-w-3xl mx-auto'>
        <div className='flex items-center justify-center gap-2'>
          {steps.map((step, index) => {
            const state = stepState(index, currentStep);
            return (
              // Keyed by position — labels aren't guaranteed unique.
              <div key={index} className='flex items-center'>
                <div className='flex items-center gap-2'>
                  <div className={stepDotVariants({variant, state})}>
                    {state === 'done' ? <Check className='h-4 w-4' /> : index + 1}
                  </div>
                  <span className={stepLabelVariants({variant, state})}>{step}</span>
                </div>
                {index < steps.length - 1 && (
                  <div className={stepConnectorVariants({variant, state})} />
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  ),
);
StepIndicator.displayName = 'StepIndicator';

export {StepIndicator};
